import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../../context/ThemeContext';
import { useContainerSearch } from '../../../utils/api-hooks/seaTracking';
import { calculateBoxes, DEFAULT_BOX_KG } from '../../../utils/groupByVarietyType';
import { predictedEta, timelinePct, formatEtaDate } from '../../../utils/shipmentSchedule';
import { formatShipChangeLine, parseShipChanges } from '../../../utils/shipChanges';
import StatusBadge from '../tracking/StatusBadge';
import { nextVesselName, liveVesselName, isHubWaiting } from '../tracking/ShipMarker';
import { hubLabel } from '../../../utils/hubWait';
import { Search, X, Loader2, Package, MapPin, Ship, ChevronDown } from 'lucide-react';

const ContainerSearch = ({
  onResultSelect, onDetailPanelChange, isDark: isDarkProp, activeShipments = [],
  variant = 'floating', heightClass = 'h-[40px]', clearSignal = 0,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDark = isDarkProp ?? theme.isDark;

  const isStrip = variant === 'strip';
  const [inputValue, setInputValue] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [selectedResult, setSelectedResult] = useState(null);
  const inputRef = useRef(null);
  const containerRef = useRef(null);

  // Debounce input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(inputValue.trim());
    }, 300);
    return () => clearTimeout(timer);
  }, [inputValue]);

  const { data, isLoading, isError } = useContainerSearch(debouncedQuery);

  // Support both old ({shipment, manifest}) and new ({shipments, manifests}) formats
  const { shipments, manifests } = useMemo(() => {
    const d = data?.data;
    if (!d) return { shipments: [], manifests: [] };
    if (d.shipments) {
      return {
        shipments: d.shipments,
        manifests: d.manifests || d.shipments.map(() => []),
      };
    }
    if (d.shipment) {
      return { shipments: [d.shipment], manifests: [d.manifest || []] };
    }
    return { shipments: [], manifests: [] };
  }, [data]);

  const localHits = useMemo(() => {
    const q = debouncedQuery.toUpperCase();
    if (q.length < 4) return [];
    return (activeShipments || []).filter((s) => {
      const hay = [
        s.container_number,
        s.all_containers,
        s.vessel,
        s.current_vessel,
        s.booking_vessel,
        s.next_vessel,
        s.arrival_port,
        s.departure_port,
        s.arrival_port_source,
        s.departure_port_source,
      ].map((v) => String(v || '').toUpperCase()).join(' ');
      return hay.includes(q);
    });
  }, [activeShipments, debouncedQuery]);

  const usingLocal = localHits.length > 0; const resultShipments = usingLocal ? localHits : shipments;
  const hasResult = resultShipments.length > 0;

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Close on Escape
  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'Escape') {
        setIsOpen(false);
        setSelectedResult(null);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  const handleSelect = useCallback((shipmentsData, manifestsData) => {
    setSelectedResult({ shipments: shipmentsData, manifests: manifestsData });
    setIsOpen(false);
    const first = shipmentsData[0];
    if (onResultSelect && first) {
      // The parent returns true when it opened its own panel for this result: no detail panel on top.
      if (onResultSelect(first, manifestsData[0] || []) === true) setSelectedResult(null);
    }
  }, [onResultSelect]);

  // Parent bumps clearSignal when it opens its own panel: drop the detail panel so it can't cover it.
  useEffect(() => {
    if (clearSignal) setSelectedResult(null);
  }, [clearSignal]);

  const handleClear = useCallback(() => {
    setInputValue('');
    setDebouncedQuery('');
    setSelectedResult(null);
    setIsOpen(false);
  }, []);

  // Notify parent when detail panel visibility changes
  useEffect(() => {
    onDetailPanelChange?.(!!selectedResult);
  }, [selectedResult, onDetailPanelChange]);

  const showDropdown = isOpen && debouncedQuery.length >= 4;

  return (
    <div ref={containerRef} className="relative">
      {/* Search input */}
      <div className={isStrip
        ? `flex ${heightClass} w-full items-center gap-[8px] rounded-[8px] border border-solid border-[#E2E5E0] bg-white px-[11px] dark:border-[#2A302C] dark:bg-[#1B1F1C]`
        : `flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg shadow-md text-xs border backdrop-blur-sm ${
        isDark
          ? 'bg-[#1d1d1d]/90 border-[#333] text-gray-200'
          : 'bg-white/90 border-[#e5e7eb] text-[#374151]'
      }`}>
        <Search size={isStrip ? 20 : 13} className={isStrip ? 'shrink-0 text-[#828382]' : (isDark ? 'text-gray-400' : 'text-[#9ca3af]')} />
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={(e) => {
            setInputValue(e.target.value.toUpperCase());
            setIsOpen(true);
            setSelectedResult(null);
          }}
          onFocus={() => debouncedQuery.length >= 4 && setIsOpen(true)}
          placeholder={isStrip
            ? t('map.containerSearch.stripPlaceholder', 'Search for container')
            : t('map.containerSearch.placeholder', 'Search container...')}
          className={isStrip
            ? "w-full bg-transparent font-['Inter'] text-[14px] font-normal text-[#374151] outline-none placeholder:text-[#828382] dark:text-[#E6E8E6]"
            : `bg-transparent outline-none w-[160px] sm:w-[200px] placeholder:text-xs ${
            isDark ? 'placeholder:text-gray-500' : 'placeholder:text-[#9ca3af]'
          }`}
        />
        {isLoading && <Loader2 size={12} className="animate-spin text-bloom-green" />}
        {inputValue && !isLoading && (
          <button onClick={handleClear} className="p-0.5 rounded hover:bg-black/10 dark:hover:bg-white/10">
            <X size={12} />
          </button>
        )}
      </div>

      {/* Dropdown results. Width is viewport-relative: the wrapper is offset
          52px from the map's left edge (clear of the zoom control), so subtract
          that plus an 8px right gutter to stay inside the map on mobile. */}
      {showDropdown && (
        <div className={`absolute top-full left-0 mt-1 rounded-lg shadow-lg border min-w-0 w-[calc(100vw-60px)] max-w-[340px] overflow-hidden z-[1001] ${
          isDark ? 'bg-[#1d1d1d] border-[#333]' : 'bg-white border-[#e5e7eb]'
        }`}>
          {isLoading && (
            <div className="flex items-center gap-2 px-3 py-3 text-xs text-[#828382]">
              <Loader2 size={14} className="animate-spin" />
              {t('map.containerSearch.searching', 'Searching...')}
            </div>
          )}
          {!isLoading && !hasResult && debouncedQuery.length >= 4 && (
            <div className="px-3 py-3 text-xs text-[#828382]">
              {t('map.containerSearch.noResults', 'No container found')}
            </div>
          )}
          {!isLoading && hasResult && (
            <div className="max-h-[280px] overflow-y-auto">
              {resultShipments.slice(0, 8).map((hit, idx) => (
                <button
                  key={`${hit.container_number || 'row'}-${hit.arrival_port || idx}`}
                  onClick={() => handleSelect([hit], [usingLocal ? [] : (manifests[idx] || [])])}
                  className={`w-full text-left px-3 py-2.5 transition-colors ${
                    isDark ? 'hover:bg-[#282a29]' : 'hover:bg-[#f3f4f6]'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <Package size={14} className="text-bloom-green flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className={`text-xs font-semibold truncate ${isDark ? 'text-white' : 'text-[#1f2937]'}`}>
                        {isHubWaiting(hit) ? t(hubLabel(hit).key, hubLabel(hit).params) : (hit.vessel || hit.current_vessel || hit.container_number)}
                      </div>
                      <div className="text-[0.625rem] text-[#828382] truncate">
                        {hit.arrival_port || '—'} · {hit.container_number || ''} · {hit.commodities || 'N/A'}
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Detail panel */}
      {selectedResult && (
        <ContainerDetailPanel
          shipments={selectedResult.shipments}
          manifests={selectedResult.manifests}
          isDark={isDark}
          t={t}
          onClose={() => setSelectedResult(null)}
        />
      )}
    </div>
  );
};

const ManifestByCommodity = ({ manifest, isDark, t }) => {
  const [open, setOpen] = useState({});
  const groups = useMemo(() => {
    const map = new Map();
    manifest.forEach((item) => {
      const key = item.commodity || '—';
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(item);
    });
    return [...map.entries()];
  }, [manifest]);
  const isOpen = (key) => open[key] !== false;
  const toggle = (key) => setOpen((prev) => ({ ...prev, [key]: prev[key] === false }));

  return (
    <div className="px-3 py-2.5">
      <div className="space-y-1.5">
        {groups.map(([commodity, items]) => {
          const cOpen = isOpen(commodity);
          const totalKg = items.reduce((sum, item) => sum + (Number(item.net_weight) || 0), 0);
          return (
            <div key={commodity}>
              <button
                type="button"
                aria-expanded={cOpen}
                onClick={() => toggle(commodity)}
                className={`w-full flex items-center gap-x-2 min-w-0 px-2 py-1.5 text-xs font-bold text-left ${
                  cOpen ? 'rounded-t' : 'rounded'
                } ${isDark ? 'bg-[#333] text-white' : 'bg-[#e5e7eb] text-[#1f2937]'}`}
              >
                <span className="inline-flex items-center gap-1.5 min-w-0 flex-1">
                  <ChevronDown size={12} className={`shrink-0 transition-transform ${cOpen ? '' : '-rotate-90'}`} />
                  <span className="min-w-0 truncate">{commodity}</span>
                </span>
                {totalKg > 0 && (
                  <span className="shrink-0 text-right font-normal whitespace-nowrap">
                    {Math.round(totalKg / DEFAULT_BOX_KG).toLocaleString()} {t('seaTracking.card.boxes', 'boxes')}
                    <span className={`ml-1 ${isDark ? 'text-gray-400' : 'text-[#828382]'}`}>
                      | {totalKg.toLocaleString('en-GB', { maximumFractionDigits: 0 })} kg
                    </span>
                  </span>
                )}
              </button>
              {cOpen && (
                <div className="space-y-1.5">
                  {items.map((item, idx) => {
                    const netWeight = Number(item.net_weight) || 0;
                    const boxInfo = calculateBoxes({
                      weight_kg: netWeight,
                      commercial_units: item.commercial_units,
                      presentation: item.presentation,
                    });
                    return (
                      <div key={`${commodity}-${idx}`} className={`rounded-b border border-t-0 p-2 text-[0.6875rem] ${
                        isDark ? 'border-[#333] bg-[#232121]' : 'border-[#e5e7eb] bg-[#f9fafb]'
                      }`}>
                        {(item.variety || item.type_of_variety) && (
                          <div className={`flex items-center gap-2 mb-1 ${isDark ? 'text-gray-300' : 'text-[#374151]'}`}>
                            {item.variety && <span>{item.variety}</span>}
                            {item.variety && item.type_of_variety && <span className={isDark ? 'text-gray-600' : 'text-[#d1d5db]'}>·</span>}
                            {item.type_of_variety && <span className={isDark ? 'text-gray-400' : 'text-[#6b7280]'}>{item.type_of_variety}</span>}
                          </div>
                        )}
                        {(boxInfo.equivBoxes82) && (
                          <div className={`rounded px-2 py-1.5 mb-1 text-[0.625rem] ${isDark ? 'bg-[#1a1a1a]' : 'bg-[#f3f4f6]'}`}>
                            <div className={`flex items-center justify-between ${isDark ? 'text-gray-200' : 'text-[#1f2937]'}`}>
                              <span className="font-medium">{t('seaTracking.card.boxes', 'boxes')}</span>
                              <span className="font-medium">{boxInfo.equivBoxes82.toLocaleString()} × {DEFAULT_BOX_KG} kg</span>
                            </div>
                          </div>
                        )}
                        <div className={`grid grid-cols-2 gap-x-3 gap-y-0.5 ${isDark ? 'text-gray-400' : 'text-[#6b7280]'}`}>
                          {item.exporter && <span>{t('map.containerSearch.exporter', 'Exporter')}: {item.exporter}</span>}
                          {item.importer && <span>{t('map.containerSearch.importer', 'Importer')}: {item.importer}</span>}
                          {item.bl_awb && <span className="col-span-2 truncate">BL: {item.bl_awb}</span>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const ShipmentSection = ({ shipment, manifest, isDark, t, showHeader }) => (
  <div>
    {/* Section header for multi-shipment */}
    {showHeader && (
      <div className={`px-3 py-2 font-semibold text-xs border-b ${
        isDark ? 'bg-[#282a29] border-[#333] text-white' : 'bg-[#f3f4f6] border-[#e5e7eb] text-[#1f2937]'
      }`}>
        {t('map.containerSearch.shipmentLabel', 'Shipment')}
        {shipment.etd && <span className="ml-1 font-normal text-[#828382]">(ETD: {shipment.etd})</span>}
      </div>
    )}

    {/* Tracking info */}
    <div className={`px-3 py-2.5 border-b ${isDark ? 'border-[#333]' : 'border-[#e5e7eb]'}`}>
      <div className={`text-[0.625rem] font-semibold uppercase tracking-wide mb-1.5 ${isDark ? 'text-gray-400' : 'text-[#828382]'}`}>
        {t('map.containerSearch.tracking', 'Tracking Info')}
      </div>
      <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
        <InfoRow label="Vessel" value={isHubWaiting(shipment) ? t(hubLabel(shipment).key, hubLabel(shipment).params) : (shipment.vessel || shipment.current_vessel)} isDark={isDark} icon={<Ship size={11} />} />
        {liveVesselName(shipment) && (
          <InfoRow label={t('seaTracking.card.currentVessel', 'current')} value={liveVesselName(shipment)} isDark={isDark} />
        )}
        {nextVesselName(shipment) && (
          <InfoRow label={t('seaTracking.card.nextVessel', 'next')} value={nextVesselName(shipment)} isDark={isDark} />
        )}
        <InfoRow label="Status" value={shipment.status?.replace('_', ' ')} isDark={isDark} />
        <InfoRow label="Origin" value={shipment.departure_port} isDark={isDark} icon={<MapPin size={11} />} />
        <InfoRow label="Destination" value={shipment.arrival_port} isDark={isDark} icon={<MapPin size={11} />} />
        <InfoRow label="ETA" value={formatEtaDate(predictedEta(shipment))} isDark={isDark} />
        <div className="col-span-2 mt-1">
          <StatusBadge
            status={shipment.status}
            delayDays={shipment.delay_days}
            etaHasUpdate={shipment.eta_has_update}
            etaUpdated={shipment.eta_updated}
            etaInitial={shipment.eta_initial}
            trackingMethod={shipment.tracking_method}
            shipment={shipment}
          />
        </div>
        <InfoRow label={t('seaTracking.card.weight', 'Weight')} value={shipment.total_weight ? `${Number(shipment.total_weight).toLocaleString()} kg` : '—'} isDark={isDark} />
        <InfoRow label="Transit" value={(() => {
          const pct = timelinePct(shipment.etd, predictedEta(shipment));
          return pct != null ? `${pct}%` : '—';
        })()} isDark={isDark} />
        {(() => {
          const line = formatShipChangeLine(shipment);
          const changes = parseShipChanges(shipment);
          if (!line && !changes.length) return null;
          return (
            <div className="col-span-2 mt-1.5">
              <div className={`text-[0.625rem] font-semibold uppercase tracking-wide mb-0.5 ${isDark ? 'text-gray-400' : 'text-[#828382]'}`}>
                {t('seaTracking.card.shipChanges', 'Ship changes')}
              </div>
              {line && <div className={isDark ? 'text-gray-200' : 'text-[#1f2937]'}>{line}</div>}
              {changes.map((c, i) => {
                const date = formatEtaDate(c.at_date);
                const fromTo = [c.from_vessel, c.to_vessel].filter(Boolean).join(' to ');
                const bits = [c.at_port, date, fromTo].filter(Boolean);
                if (!bits.length) return null;
                return (
                  <div key={`${c.at_port}-${i}`} className={isDark ? 'text-gray-400' : 'text-[#4b5563]'}>
                    {bits.join(' · ')}
                  </div>
                );
              })}
            </div>
          );
        })()}
      </div>
    </div>

    {/* Manifest — grouped by commodity, click header to collapse varieties */}
    {manifest.length > 0 && (
      <ManifestByCommodity manifest={manifest} isDark={isDark} t={t} />
    )}
  </div>
);

const ContainerDetailPanel = ({ shipments, manifests, isDark, t, onClose }) => {
  const isMulti = shipments.length > 1;

  return (
    <div
      className={`absolute top-full left-0 mt-1 rounded-lg shadow-xl border z-[1002] w-[calc(100vw-60px)] sm:w-[420px] overflow-y-auto ${
        isDark ? 'bg-[#1d1d1d] border-[#333]' : 'bg-white border-[#e5e7eb]'
      }`}
      style={{ maxHeight: 'calc(100vh - 220px)' }}
    >
      {/* Header */}
      <div className={`sticky top-0 z-10 flex items-center justify-between px-3 py-2.5 border-b ${
        isDark ? 'bg-[#1d1d1d] border-[#333]' : 'bg-white border-[#e5e7eb]'
      }`}>
        <div className="flex items-center gap-2">
          <Package size={14} className="text-bloom-green" />
          <span className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-[#1f2937]'}`}>
            {shipments[0]?.container_number}
          </span>
          {isMulti && (
            <span className="text-[0.625rem] text-[#828382]">
              ({shipments.length} {t('map.containerSearch.shipmentLabel', 'shipments')})
            </span>
          )}
        </div>
        <button onClick={onClose} className="p-1 rounded hover:bg-black/10 dark:hover:bg-white/10">
          <X size={14} className={isDark ? 'text-gray-400' : 'text-[#6b7280]'} />
        </button>
      </div>

      {/* Stacked shipment sections */}
      {shipments.map((shipment, sIdx) => (
        <ShipmentSection
          key={shipment.etd || shipment.eta || sIdx}
          shipment={shipment}
          manifest={manifests[sIdx] || []}
          isDark={isDark}
          t={t}
          showHeader={isMulti}
        />
      ))}
    </div>
  );
};

const InfoRow = ({ label, value, isDark, icon }) => (
  <div className="flex items-center gap-1">
    {icon && <span className={isDark ? 'text-gray-500' : 'text-[#9ca3af]'}>{icon}</span>}
    <span className={isDark ? 'text-gray-400' : 'text-[#828382]'}>{label}:</span>
    <span className={`font-medium capitalize ${isDark ? 'text-white' : 'text-[#1f2937]'}`}>{value || '—'}</span>
  </div>
);

export default ContainerSearch;
